/* eslint-disable array-callback-return */
import DOMPurify from "dompurify";
import { convertToHTML } from "draft-convert";
import { EditorState, convertFromRaw, convertToRaw, RawDraftContentState } from "draft-js";
import { ContentState } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import { QueryClient } from "@tanstack/react-query";
import { NODE_TYPE } from "./constants";
import type {
  FlowNode,
  FlowEdge,
  FlowCompleteResult,
  JumpToValidationResult,
  NodeMessageValidationResult,
  ButtonNodeValidationResult,
  AtsValueMissingResult,
} from "../types";

export const getDraftToHTML = (data: EditorState | string): string => {
  if (typeof data === "string") return data;
  return DOMPurify.sanitize(convertToHTML(data.getCurrentContent()));
};

export const getHTMLToDraft = (html: string | EditorState): EditorState => {
  if (typeof html === "string") {
    const contentBlock = htmlToDraft(html);
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(
        contentBlock.contentBlocks
      );
      const editorState = EditorState.createWithContent(contentState);
      return editorState;
    }
    return EditorState.createEmpty();
  }
  return html;
};

interface MentionEntity {
  type: string;
  data: {
    text: string;
    [key: string]: unknown;
  };
}

const convertMentionToSpan = (entity: MentionEntity, text: string): string | undefined => {
  if (entity.type === "MENTION") {
    return `<span>${entity.data.text}</span>`;
  }
};

interface NodeWithMessages {
  data?: {
    messages?: Array<{
      text?: EditorState;
      [key: string]: unknown;
    }>;
    message?: EditorState;
    [key: string]: unknown;
  };
  message?: EditorState;
  [key: string]: unknown;
}

export const beforeSendNodes = <T extends NodeWithMessages>(nodes: T[] | undefined): T[] | undefined => {
  return nodes?.map((node) => {
    if (node?.data?.messages) {
      const messages = node?.data?.messages?.map((message) => {
        return {
          ...message,
          text: convertToRaw(message?.text?.getCurrentContent() as any),
          html: draftToHtml(
            convertToRaw(message?.text?.getCurrentContent() as any),
            {},
            false,
            convertMentionToSpan as any
          ),
        };
      });
      return { ...node, data: { ...node.data, messages } };
    } else if (node?.data?.message) {
      return {
        ...node,
        data: {
          ...node?.data,
          message: convertToRaw(node?.data?.message?.getCurrentContent() as any),
          html: draftToHtml(
            convertToRaw(node?.data?.message?.getCurrentContent() as any),
            {},
            false,
            convertMentionToSpan as any
          ),
        },
      };
    } else if (node?.message) {
      return {
        ...node,
        message: convertToRaw(node?.message?.getCurrentContent() as any),
        html: draftToHtml(
          convertToRaw(node?.message?.getCurrentContent() as any),
          {},
          false,
          convertMentionToSpan as any
        ),
      };
    }
    return node;
  });
};

interface NodeWithRawMessages {
  data?: {
    messages?: Array<{
      text?: RawDraftContentState;
      [key: string]: unknown;
    }>;
    message?: RawDraftContentState;
    [key: string]: unknown;
  };
  message?: RawDraftContentState;
  [key: string]: unknown;
}

export const convertToEditorState = <T extends NodeWithRawMessages>(nodes: T[] | undefined): T[] | undefined => {
  return nodes?.map((node) => {
    if (node?.data?.messages) {
      const messages = node?.data?.messages?.map((message) => {
        return {
          ...message,
          text: EditorState.createWithContent(
            convertFromRaw(message.text as RawDraftContentState || "" as any)
          ),
        };
      });
      return { ...node, data: { ...node.data, messages } };
    } else if (node?.data?.message) {
      return {
        ...node,
        data: {
          ...node?.data,
          message: EditorState.createWithContent(
            convertFromRaw(node?.data?.message as RawDraftContentState || "" as any)
          ),
        },
      };
    } else if (node?.message) {
      return {
        ...node,
        message: EditorState.createWithContent(
          convertFromRaw(node?.message as RawDraftContentState || "" as any)
        ),
      };
    }
    return node;
  });
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 10,
    },
  },
});

export function areArraysSame<T>(arr1: T[], arr2: T[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }
  return true;
}

export function getSizeFromBase64String(base64String: string): number {
  const sizeInBytes = base64String.length;
  const sizeInMB = sizeInBytes / (1024 * 1024);
  return sizeInMB;
}

type DebouncedFunction<T extends (...args: any[]) => any> = {
  (...args: Parameters<T>): void;
  flush: () => void;
};

export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): DebouncedFunction<T> {
  let timeoutID: ReturnType<typeof setTimeout> | undefined;
  let lastArgs: Parameters<T> | undefined;

  const run = (): void => {
    if (lastArgs) {
      fn(...lastArgs);
      lastArgs = undefined;
    }
  };

  const debounced = (...args: Parameters<T>): void => {
    clearTimeout(timeoutID);
    lastArgs = args;
    timeoutID = window.setTimeout(run, delay);
  };

  debounced.flush = (): void => {
    clearTimeout(timeoutID);
    run();
  };

  return debounced;
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;
  return function (this: unknown, ...args: Parameters<T>): void {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export function afterAll(fn: () => void): () => void {
  let calls = 0;
  return function (): void {
    calls++;
    if (calls >= 2) {
      fn();
    }
  };
}

interface FlowNodeLike {
  id: string;
  type: string;
  data?: {
    value?: string;
    questionId?: string;
    isMessageEmpty?: boolean;
    atsValue?: string;
    buttons?: Array<{ titleText?: string | null }>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface FlowEdgeLike {
  source: string;
  sourceHandle?: string;
  target: string;
  [key: string]: unknown;
}

export function checkIfFlowIsComplete(
  nodes: FlowNodeLike[],
  edges: FlowEdgeLike[]
): FlowCompleteResult {
  let isComplete = true;
  let errNode: FlowNodeLike | null = null;
  
  nodes.forEach((node) => {
    if (node.type === NODE_TYPE.JUMP_TO_NODE || node.type === NODE_TYPE.END_NODE || node.type === NODE_TYPE.JUMP_TO_PROFILE_NODE) {
      return;
    }
    if (node.type === NODE_TYPE.CONDITIONAL_NODE || node.type === NODE_TYPE.SERVICE_CALL_NODE) {
      if (!checkSpecialNodeEdges(edges, node.id)) {
        isComplete = false;
        errNode = node as any;
        return;
      }
      return;
    }
    if (!isBottomEdgeConnected(edges, node.id)) {
      isComplete = false;
      errNode = node as any;
      return;
    }
  });
  
  return { isComplete, errNode: errNode as FlowNode | null };
}

const isBottomEdgeConnected = (edges: FlowEdgeLike[], id: string): boolean =>
  edges.findIndex(
    (edge) => edge.source === id && edge.sourceHandle === "bottom"
  ) !== -1;

const checkSpecialNodeEdges = (edges: FlowEdgeLike[], id: string): boolean => {
  const foundSuccessEdge = edges.findIndex(
    (edge) => edge.source === id && edge.sourceHandle === "bottom-success"
  ) !== -1;
  const foundFailureEdge = edges.findIndex(
    (edge) => edge.source === id && edge.sourceHandle === "bottom-failure"
  ) !== -1;
  return foundSuccessEdge && foundFailureEdge;
};

export function checkAtsValueMissing(nodes: FlowNodeLike[]): AtsValueMissingResult {
  let atsValueMissingNode: FlowNodeLike | null = null;
  
  const totalAtsNodes = nodes.filter((node) =>
    node.type === NODE_TYPE.BUTTONS_NODE || 
    node.type === NODE_TYPE.COLLECT_INPUT_NODE || 
    node.type === NODE_TYPE.FILE_UPLOAD_NODE || 
    node.type === NODE_TYPE.CALENDAR_NODE
  );

  const missingAtsNodes = totalAtsNodes.filter((node) => {
    if (!node.data?.hasOwnProperty("atsValue") || node?.data?.atsValue === "" || node?.data?.atsValue === null) {
      atsValueMissingNode = node;
      return true;
    }
    return false;
  });
  
  const isAtsValueMissing = totalAtsNodes.length === missingAtsNodes.length;
  return { isAtsValueMissing, atsValueMissingNode: atsValueMissingNode as FlowNode | null };
}

export function validateJumpToNode(nodes: FlowNodeLike[]): JumpToValidationResult {
  let isJumpToNodeValid = true;
  let jumpToNode: FlowNodeLike | null = null;
  let message: string | null = null;
  
  nodes.forEach((node) => {
    if (!isJumpToNodeValid) return;
    if (node.type === NODE_TYPE.JUMP_TO_NODE || node.type === NODE_TYPE.JUMP_TO_PROFILE_NODE) {
      if (node?.data?.value === "0" || node?.data?.questionId === "0") {
        isJumpToNodeValid = false;
        if (node?.data?.value === "0") {
          message = "profile";
        } else {
          if (node?.data?.questionId === "0") {
            message = "question";
          }
        }
        jumpToNode = node;
        return;
      }
    }
  });
  
  return { isJumpToNodeValid, jumpToNode: jumpToNode as FlowNode | null, message };
}

export function validateNodeMessages(nodes: FlowNodeLike[]): NodeMessageValidationResult {
  let isNodeMessageValid = true;
  let nodeMessage: FlowNodeLike | null = null;
  
  nodes.forEach((node) => {
    if (!isNodeMessageValid) return;
    if (node?.data?.isMessageEmpty) {
      isNodeMessageValid = false;
      nodeMessage = node;
      return;
    }
  });
  
  return { isNodeMessageValid, nodeMessage: nodeMessage as FlowNode | null };
}

export function validateButtonNodes(nodes: FlowNodeLike[]): ButtonNodeValidationResult {
  let isButtonNodeValid = true;
  let buttonNode: FlowNodeLike | null = null;
  
  nodes.forEach((node) => {
    if (!isButtonNodeValid) return;
    if (node.type === NODE_TYPE.BUTTONS_NODE) {
      node?.data?.buttons?.map(function (button) {
        if (button.titleText === "" || button.titleText === null) {
          isButtonNodeValid = false;
          buttonNode = node;
        }
      });
    }
  });
  
  return { isButtonNodeValid, buttonNode: buttonNode as FlowNode | null };
}
